import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-orange-500 text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-orange-400 opacity-30" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-orange-600 opacity-30" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <div className="w-11 h-11 rounded-xl bg-white grid place-items-center overflow-hidden">
              <img src="/L360.png" alt="Losung360" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <div className="text-2xl font-bold tracking-tight leading-tight">Losung360</div>
              <div className="text-xs font-medium tracking-widest uppercase text-white/80">LeadHub</div>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h2 className="text-4xl font-bold leading-tight tracking-tight">
            A single source of truth for your sales pipeline.
          </h2>
          <p className="mt-4 text-white/80 text-sm leading-relaxed">
            Capture leads from every channel, qualify them through a disciplined 10-stage funnel,
            and convert faster with role-based access and configurable SOPs.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3">
            {["Multi-channel capture", "Stage SOPs", "Audit & ownership"].map((t) => (
              <div key={t} className="bg-white/10 backdrop-blur rounded-lg p-3 text-xs font-medium">
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/60">© 2026 Losung360 LMS</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm"
        >
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Sign in</h1>
            <p className="text-sm text-slate-500 mt-1">Enter your credentials to access the LMS.</p>
          </div>

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Email
          </label>
          <div className="relative mb-4">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="login-email-input"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm"
              placeholder="you@company.com"
            />
          </div>

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Password
          </label>
          <div className="relative mb-2">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm"
              placeholder="••••••••"
            />
          </div>
          <div className="mb-5 text-right">
            <Link
              data-testid="forgot-password-link"
              to="/forgot-password"
              className="text-xs font-medium text-orange-600 hover:text-orange-700"
            >
              Forgot password?
            </Link>
          </div>

          <button
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium py-2.5 rounded-md transition-colors flex items-center justify-center gap-1.5 shadow-sm"
          >
            {loading ? "Signing in…" : "Sign in"}
            {!loading && <ArrowRight size={14} />}
          </button>

        </form>
      </div>
    </div>
  );
}
